import { googleLogout } from '@react-oauth/google';

import { storage, storageKeys } from '@/constants/storage';
import type { AppleSignInError } from '@/types/auth';
import { AppleSignInErrorType } from '@/types/auth';

/**
 * Type guard to check if error is Apple Sign-in specific
 * @param error - The error to check
 * @returns true if the error is an AppleSignInError
 */
export function isAppleSignInError(error: unknown): boolean {
  return (
    !!error &&
    typeof error === 'object' &&
    'type' in error &&
    Object.values(AppleSignInErrorType).includes(
      error.type as AppleSignInErrorType,
    )
  );
}

/**
 * Type guard to check if response is a valid authentication response
 * @param response - The response to check
 * @returns true if the response is an AuthenticationResponse
 */
export function isAuthenticationResponse(response: unknown): boolean {
  return (
    !!response &&
    typeof response === 'object' &&
    'token' in response &&
    'user' in response &&
    typeof response.token === 'string'
  );
}

/**
 * Extracts user data from Apple credentials for backend API call
 * @param credentials - Apple authentication credentials
 * @returns Formatted data for backend API
 */
export function extractAppleUserData(credentials: {
  fullName?: { givenName?: string | null; familyName?: string | null } | null;
  email?: string | null;
}) {
  return {
    firstName: credentials.fullName?.givenName || null,
    lastName: credentials.fullName?.familyName || null,
    email: credentials.email || null,
  };
}

/**
 * Creates a user-friendly error message from Apple Sign-in error
 * @param error - The Apple Sign-in error
 * @returns User-friendly error message
 */
export function getAppleSignInErrorMessage(error: AppleSignInError): string {
  switch (error.type) {
    case AppleSignInErrorType.USER_CANCELLED:
      return 'Sign-in was cancelled';
    case AppleSignInErrorType.APPLE_AUTH_FAILED:
      return 'Apple authentication failed. Please try again.';
    case AppleSignInErrorType.BACKEND_FAILED:
      return 'Unable to complete sign-in. Please try again.';
    case AppleSignInErrorType.NETWORK_ERROR:
      return 'Network error. Please check your connection and try again.';
    case AppleSignInErrorType.UNAVAILABLE:
      return 'Apple Sign-in is not available on this device';
    case AppleSignInErrorType.INVALID_RESPONSE:
      return 'Invalid response from Apple. Please try again.';
    default:
      return 'An unexpected error occurred. Please try again.';
  }
}

/**
 * Returns true when the Apple JS SDK error represents the user closing the popup.
 * Apple sends { error: "popup_closed_by_user" } in this case.
 */
export function isApplePopupClosedError(error: unknown): boolean {
  return (
    !!error &&
    typeof error === 'object' &&
    'error' in error &&
    error.error === 'popup_closed_by_user'
  );
}

type GisWindow = Window & {
  google?: { accounts?: { id?: { revoke?: (hint: string) => void } } };
};

/**
 * Logs out from Google
 * @returns void
 */
export async function logoutAuthProviders(): Promise<void> {
  const email = storage.getString(storageKeys.googleSignInEmail);
  if (email) {
    (window as GisWindow).google?.accounts?.id?.revoke?.(email);
    storage.remove(storageKeys.googleSignInEmail);
  }
  googleLogout();
}
